import { defineStore } from 'pinia'
import ServiceStore, { ServiceState } from '@/modules/myServices/types/store/ServiceStoreI'
import { ServiceGroupI, UpcomingInvoiceI } from '@/modules/myServices/types/api/Service'
import {
  DashboardSectionMessageI,
  DashboardServiceTypeI,
  DashboardServicesGroupsI,
} from '@/modules/myServices/types/api/DashboardServices'
import ServiceGroupItem from '@/modules/myServices/entities/ServiceGroupItem'
import DashboardServiceItem from '@/modules/myServices/entities/DashboardServiceItem'
import DashboardSslItem from '@/modules/myServices/entities/DashboardSslItem'
import DashboardDomainItem from '@/modules/myServices/entities/DashboardDomainItem'
import UpcomingInvoiceItem from '@/modules/myServices/entities/UpcomingInvoiceItem'
import { CANCELLED_SERVICE_STATUSES, SECTION_UNAVAILABLE } from '@/constants/dashboardServiceTypes'
import { useUserStore } from '@/modules/auth/store'

export const useServiceStore: ServiceStore = defineStore('service', {
  state: () =>
    ({
      servicesGroups: null,
      // `undefined` per section, not `[]` — the tables must not read "not loaded yet" as "none".
      dashboardServices: {
        vps: undefined,
        vpc: undefined,
        wordpress: undefined,
        ssl: undefined,
        domain: undefined,
      },
      dashboardCurrency: '',
      dashboardMessages: [],
      // The dashboard's "Show cancelled services" switch. Off is the default, so the first
      // load already asks the backend to leave cancelled services out.
      showCancelledServices: false,
      upcomingInvoices: undefined,
      userNotifications: null,
    }) as ServiceState,
  getters: {
    getUpcomingCount(state) {
      return state.userNotifications?.invoiceSummary.unpaidInvoiceCount || 0
    },
    getOverdueCount(state) {
      return state.userNotifications?.invoiceSummary.overdueInvoiceCount || 0
    },
    getInvoiceId(state) {
      return state.userNotifications?.invoiceSummary.invoiceId
    },
    getOverdueAmount(state) {
      if (!state.userNotifications) return ''
      return `${useUserStore().getUserCurrencyCode}${state.userNotifications?.invoiceSummary.overdueInvoiceTotal}`
    },
    getVpsServices(state) {
      return state.dashboardServices.vps ?? []
    },
    getVpcServices(state) {
      return state.dashboardServices.vpc ?? []
    },
    getWordpressServices(state) {
      return state.dashboardServices.wordpress ?? []
    },
    getSslServices(state) {
      return state.dashboardServices.ssl ?? []
    },
    getDomainServices(state) {
      return state.dashboardServices.domain ?? []
    },
    getUpcomingInvoicesList(state) {
      return state.upcomingInvoices ?? []
    },
    isVpcLimited() {
      return (
        this.getVpcServices.filter(
          (item: DashboardServiceItem) => item.status === 'active' || item.status === 'suspended',
        ).length >= 2
      )
    },
    /**
     * Per-section failure text, keyed by type. An empty section with an entry here means "we
     * could not load this", not "you own none of these" — the tables have to tell those apart
     * or they promise an empty account to someone whose domains simply did not arrive.
     */
    getSectionErrors(state) {
      return state.dashboardMessages.reduce(
        (errors: Partial<Record<DashboardServiceTypeI, string>>, message: DashboardSectionMessageI) => {
          if (message.code !== SECTION_UNAVAILABLE || !message.details?.type) return errors
          errors[message.details.type] = message.message
          return errors
        },
        {} as Partial<Record<DashboardServiceTypeI, string>>,
      )
    },
    domainExpirations(state) {
      if (!state.userNotifications) return null
      return state.userNotifications.domainExpirations
    },
  },
  actions: {
    async getProductGroups() {
      const response = await this.$services.myServices.loadProductGroups()
      this.servicesGroups = response.map((item: ServiceGroupI) => new ServiceGroupItem(item))
    },
    /**
     * The switch is a reload, not a client-side filter: an excluded service never reaches the
     * SPA, so flipping it has to come back through here.
     */
    async getDashboardServices() {
      const response = await this.$services.myServices.loadDashboardServices(
        this.showCancelledServices ? [] : CANCELLED_SERVICE_STATUSES,
      )
      if (!response) return

      const services = (response.data?.services ?? {}) as Partial<DashboardServicesGroupsI>
      const currency = response.meta?.currency || useUserStore().getUserCurrencyCode || ''

      this.dashboardCurrency = currency
      this.dashboardMessages = response.meta?.messages ?? []
      this.dashboardServices = {
        vps: (services.vps ?? []).map(item => new DashboardServiceItem(item, currency)),
        vpc: (services.vpc ?? []).map(item => new DashboardServiceItem(item, currency)),
        wordpress: (services.wordpress ?? []).map(item => new DashboardServiceItem(item, currency)),
        ssl: (services.ssl ?? []).map(item => new DashboardSslItem(item, currency)),
        domain: (services.domain ?? []).map(item => new DashboardDomainItem(item, currency)),
      }
    },
    setShowCancelledServices(showCancelled: boolean) {
      this.showCancelledServices = showCancelled
    },
    async getUpcomingInvoices() {
      const response = await this.$services.myServices.loadUpcomingInvoices()
      this.upcomingInvoices =
        response.invoices?.map((item: UpcomingInvoiceI) => {
          return new UpcomingInvoiceItem(item)
        }) ?? []
    },
    async getInvoicePdf(id: number) {
      return await this.$services.myServices.loadInvoicePdf(id)
    },
    async getUserNotificationsData() {
      const response = await this.$services.myServices.getUserNotificationsData()
      if (response) {
        this.userNotifications = response
      }
    },
  },
})
